/**
 * Category presets mirror MonitoringParameters.js's CATEGORY_PRESETS so that
 * an inspection's category always lines up with how parameters are actually
 * configured (org-configurable "what to monitor" is the source of truth,
 * not a fixed inspection-specific list).
 */
export const CATEGORY_PRESETS = [
  'osh', 'environmental', 'labor', 'social', 'stakeholder', 'biodiversity',
  'culturalHeritage', 'indigenousPeoples', 'gbvPrevention', 'childLabor', 'permits', 'roadSafety',
];

export const CATEGORY_LABEL = {
  osh: 'Occupational Safety & Health (OHS)',
  environmental: 'Environmental',
  labor: 'Labor',
  social: 'Social (Welfare, GRM, Housekeeping)',
  stakeholder: 'Stakeholder Engagement',
  biodiversity: 'Biodiversity',
  culturalHeritage: 'Cultural Heritage',
  indigenousPeoples: 'Indigenous Peoples',
  gbvPrevention: 'GBV Prevention',
  childLabor: 'Child Labor',
  permits: 'Permits & Consents',
  roadSafety: 'Road Safety',
};

/**
 * Icons for category tiles/pages, keyed the same way as CATEGORY_LABEL.
 * Falls back to a generic icon for anything not listed (custom categories).
 */
export const CATEGORY_ICON = {
  osh: 'fa-hard-hat',
  environmental: 'fa-leaf',
  labor: 'fa-users',
  social: 'fa-people-carry',
  stakeholder: 'fa-handshake',
  biodiversity: 'fa-tree',
  culturalHeritage: 'fa-landmark',
  indigenousPeoples: 'fa-map-marked',
  gbvPrevention: 'fa-shield-alt',
  childLabor: 'fa-child',
  permits: 'fa-file-signature',
  roadSafety: 'fa-car-crash',
};

/**
 * Display label for any category value, including org-defined custom
 * categories (free text, e.g. "tailings") that have no entry in
 * CATEGORY_LABEL. Known presets use their curated label; anything else is
 * title-cased (and camelCase/snake_case/kebab-case is split into words
 * first) so a custom category still reads like "Tailings Pond" instead of
 * whatever raw casing the user happened to type.
 */
export const formatCategory = (category) => {
  if (!category) return '';
  if (CATEGORY_LABEL[category]) return CATEGORY_LABEL[category];
  return String(category)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
};

// Maps each CATEGORY_PRESETS value to the Organisation/Project
// trackingCategories key that gates it. `culturalHeritage` and
// `indigenousPeoples` share one org-level toggle (labelled "Cultural
// Heritage & Indigenous Peoples" in TRACKING_CATEGORIES); `social` has no
// corresponding toggle at all, so it's left out here and always offered.
const PRESET_TO_TRACKING_KEY = {
  osh: 'osh',
  environmental: 'environmental',
  labor: 'labor',
  stakeholder: 'stakeholder',
  biodiversity: 'biodiversity',
  culturalHeritage: 'culturalHeritage',
  indigenousPeoples: 'culturalHeritage',
  gbvPrevention: 'gbvPrevention',
  childLabor: 'childLabor',
  permits: 'permits',
  roadSafety: 'roadSafety',
};

/**
 * Category options for a category-picking <select>, scoped to what this org
 * actually tracks - the shared convention originally established in
 * FormBuilderModal.js. `trackingCategories` null (org hasn't onboarded yet)
 * falls back to offering every preset, matching pre-onboarding behaviour
 * elsewhere in the app. `existingValue` (e.g. a parameter/form/risk
 * assessment's current category) is always kept selectable even if the org
 * later disabled that category, so an edit never silently changes the value
 * out from under the user.
 */
export const getTrackedCategoryOptions = (trackingCategories, customCategories = [], existingValue = null) => {
  const presets = CATEGORY_PRESETS.filter((preset) => {
    const trackingKey = PRESET_TO_TRACKING_KEY[preset];
    if (!trackingKey) return true; // e.g. 'social' - no toggle to gate on
    if (!trackingCategories) return true; // not onboarded yet - show everything
    return !!trackingCategories[trackingKey];
  });

  const options = [
    ...presets.map((c) => ({ value: c, label: CATEGORY_LABEL[c] || c })),
    ...customCategories.map((c) => ({ value: c, label: formatCategory(c) })),
  ];

  if (existingValue && !options.some((o) => o.value === existingValue)) {
    options.push({ value: existingValue, label: CATEGORY_LABEL[existingValue] || formatCategory(existingValue) });
  }

  return options;
};
